import { Form, type GetRef, type Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { toLocalInputValue, type CaptureFormValues } from '../policies/capture-step.policy';
import { useDriverCapture } from './use-driver-capture';

const freshValues = (): Partial<CaptureFormValues> => ({ when: toLocalInputValue(new Date()) });

/**
 * The capture form's own state: what is chosen, the photos, and putting focus back on the step picker after a save so the next step on the call is a
 * keystroke away. The form is disabled while saving and a disabled field cannot take focus, so focus waits until saving has ended.
 */
export function useDriverCaptureForm(tripId: string, driverName: string) {
  const [form] = Form.useForm<CaptureFormValues>();
  const step = Form.useWatch('step', form);
  const picker = useRef<GetRef<typeof Select>>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const focusedAfter = useRef(0);
  const capture = useDriverCapture(tripId, driverName);

  useEffect(() => {
    if (capture.isSubmitting || focusedAfter.current === savedCount) return;
    focusedAfter.current = savedCount;
    picker.current?.focus();
  }, [capture.isSubmitting, savedCount]);

  async function submit(values: CaptureFormValues) {
    if (!(await capture.record(values, photos))) return;
    form.resetFields();
    form.setFieldsValue(freshValues());
    setPhotos([]);
    setPhotoInputKey((key) => key + 1);
    setSavedCount((count) => count + 1);
  }

  return { ...capture, form, step, picker, photoInputKey, setPhotos, submit, initialValues: freshValues() };
}
